<template>
  <div>
    <v-progress-linear
      v-if="competingDifficultyLevelsLoading"
      indeterminate
      class="mb-10"
    />

    <CompetingDifficultyLevelForm
      v-if="
        $auth.checkUserCanPerformMutationsInScopeAny(
          'competingDifficultyLevelCreate'
        ) ||
        $auth.checkUserCanPerformMutationsInScopeAny(
          'competingDifficultyLevelUpdate'
        )
      "
      ref="competingLevelForm"
      @refetch="() => refetchCompetingDifficultyLevels()"
    />
    <div
      v-for="group in showSpecificCompetingGroups"
      :key="group.id"
      class="mb-10"
    >
      <AppTableHeader>
        <template #left>
          {{
            $t(
              "common:discipline." + group.discipline.name,
              group.discipline.name
            )
          }}
          -
          {{
            $t(
              `common:competingDifficultyLevels.rankedEntity.${group.rankedEntity}.label`,
              group.rankedEntity
            )
          }}
          - {{ group.name }}
        </template>
        <template #right>
          <v-btn
            v-if="
              $auth.checkUserCanPerformMutationsInScopeAny(
                'competingDifficultyLevelCreate'
              )
            "
            class="btn-with-icon mx-2"
            variant="text"
            data-test="competingDifficultyLevelCreate"
            @click="() => openNewForm(group)"
          >
            <div class="bg-edit btn-icon-with-text">
              <v-icon size="small" class="text-white"> $iconPlus </v-icon>
            </div>
            <span class="text-white">{{ $t("common:button.add", "Add") }}</span>
          </v-btn>
        </template>
      </AppTableHeader>
      <table class="entsys--table">
        <thead class="text-left hidden-sm-and-down">
          <tr>
            <th>
              {{
                $t(
                  "common:competingDifficultyLevels.rankedEntity.name.label",
                  "Name"
                )
              }}
            </th>
            <th>
              {{ $t("common:competingDifficultyLevels.level.label", "Level") }}
            </th>
            <th>
              {{
                $t(
                  "common:competingDifficultyLevels.validityPeriod.label",
                  "Validity period"
                )
              }}
            </th>
            <th v-if="showPointsHeader(competingDifficultyLevels[group.id])">
              {{
                $t(
                  "common:competingDifficultyLevels.pointsBalance.label",
                  "Points"
                )
              }}
            </th>
            <th>
              {{
                $t("common:competingDifficultyLevels.details.label", "Details")
              }}
            </th>
            <th />
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="competitionDifficultyLevel in competingDifficultyLevels[
              group.id
            ]"
            :key="competitionDifficultyLevel.id + '-competing-level'"
            :data-test="
              'competing-difficulty-level-id-' + competitionDifficultyLevel.id
            "
          >
            <td>
              <div class="d-block d-md-flex">
                <AppShowPersonNameWithIdentifiers
                  v-if="['PERSON', 'PAIR'].includes(group.rankedEntity)"
                  :person="competitionDifficultyLevel.person"
                />

                <span
                  v-if="['PAIR'].includes(group.rankedEntity)"
                  class="mx-1 hidden-md-and-down"
                >
                  |
                </span>
                <div
                  :class="{
                    'mb-2': ['PAIR'].includes(group.rankedEntity),
                    'hidden-lg-and-up': true
                  }"
                />
                <AppShowHorseName
                  v-if="['HORSE', 'PAIR'].includes(group.rankedEntity)"
                  :horse="competitionDifficultyLevel.horse"
                  show-identifier
                />
              </div>
            </td>
            <td>
              {{
                competitionDifficultyLevel.competingDifficultyLevelDefinition
                  .name
              }}

              <div class="hidden-md-and-up">
                {{ $dayjs.date(competitionDifficultyLevel.validFrom) }} -
                {{
                  competitionDifficultyLevel.validUntil
                    ? $dayjs.date(competitionDifficultyLevel.validUntil)
                    : "..."
                }}
              </div>
              <div
                v-if="competitionDifficultyLevel.showPoints"
                class="hidden-md-and-up"
              >
                {{
                  $t(
                    "common:competingDifficultyLevels.pointsBalance.label",
                    "Points"
                  )
                }}: {{ competitionDifficultyLevel.pointsBalance }}
              </div>
              <div class="hidden-md-and-up">
                <AppChip
                  :color="
                    competitionDifficultyLevel.status == 'ACTIVE'
                      ? 'edit-green'
                      : 'muted'
                  "
                  :text-color="'text-white'"
                >
                  {{
                    $t(
                      `common:competingDifficultyLevels.status.${competitionDifficultyLevel.status}.label`,
                      competitionDifficultyLevel.status
                    )
                  }}
                </AppChip>
              </div>
            </td>
            <td class="hidden-sm-and-down">
              {{ $dayjs.date(competitionDifficultyLevel.validFrom) }} -
              {{
                competitionDifficultyLevel.validUntil
                  ? $dayjs.date(competitionDifficultyLevel.validUntil)
                  : "..."
              }}
            </td>
            <td
              v-if="competitionDifficultyLevel.showPoints"
              class="hidden-sm-and-down"
            >
              {{ competitionDifficultyLevel.pointsBalance }}
            </td>
            <td class="hidden-sm-and-down">
              <AppChip
                :color="
                  competitionDifficultyLevel.status == 'ACTIVE'
                    ? 'edit-green'
                    : 'muted'
                "
                :text-color="'text-white'"
              >
                {{
                  $t(
                    `common:competingDifficultyLevels.status.${competitionDifficultyLevel.status}.label`,
                    competitionDifficultyLevel.status
                  )
                }}
              </AppChip>
            </td>
            <td class="text-end">
              <v-btn-icon
                v-if="
                  $allowedMutation(
                    competitionDifficultyLevel.allowedMutations,
                    'competingDifficultyLevelUpdate'
                  )
                "
                icon
                class="bg-edit-green ma-2"
                size="small"
                data-test="competingDifficultyLevelUpdate"
                @click="() => openEditForm(competitionDifficultyLevel)"
              >
                <v-icon size="small" class="text-white"> $edit </v-icon>
              </v-btn-icon>
              <v-btn-icon
                v-if="
                  $allowedMutation(
                    competitionDifficultyLevel.allowedMutations,
                    'competingDifficultyLevelDelete'
                  )
                "
                class="bg-remove ma-2"
                data-test="competingDifficultyLevelDelete"
                :aria-label="$t('common:deleteButton.alt')"
                icon
                size="small"
                @click="
                  deleteCompetingDifficultyLevel(
                    competitionDifficultyLevel,
                    $event
                  )
                "
              >
                <v-icon color="white" size="small"> $iconTrash </v-icon>
                <v-progress-circular
                  v-show="false"
                  color="white"
                  size="21"
                  indeterminate
                />
              </v-btn-icon>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>
<script>
import { ref, computed, defineComponent } from "vue";
import { groupBy, cloneDeep } from "lodash-es";
import gql from "fraql";
import { apolloClient } from "@/plugins/apollo";
import { useQuery } from "@vue/apollo-composable";
import { useTranslation } from "i18next-vue";
import { useCompetingDifficultyLevelGroupHelper } from "@/composables/masterdata/competing/competingDifficultyLevelGroups";

import AppTableHeader from "@/components/elements/table/AppTableHeader.vue";
import AppShowHorseName from "@AppShows/horse/AppShowHorseName.vue";
import AppChip from "@elements/AppChip.vue";
import CompetingDifficultyLevelForm from "@elements/competing/CompetingDifficultyLevelForm.vue";
import AppShowPersonNameWithIdentifiers from "@AppShows/person/AppShowPersonNameWithIdentifiers.vue";

import {
  injectAppConfirm,
  injectSnackbarError
} from "@/composables/injections";
import { showLoadingButton } from "@/utils/showLoadingButton";

export default defineComponent({
  name: "AppCompetingLevels",
  components: {
    AppTableHeader,
    AppShowHorseName,
    AppChip,
    CompetingDifficultyLevelForm,
    AppShowPersonNameWithIdentifiers
  },
  props: {
    personId: {
      type: String,
      default: null
    },
    horseId: {
      type: String,
      default: null
    }
  },
  setup(props) {
    const { appConfirm } = injectAppConfirm();
    const { snackbarServerError } = injectSnackbarError();
    const { t } = useTranslation();
    const { masterCompetingDifficultyLevelGroupsActive } =
      useCompetingDifficultyLevelGroupHelper();

    const entityType = computed(() => {
      if (props.personId) return "person";
      else if (props.horseId) return "horse";
      return undefined;
    });

    const showSpecificCompetingGroups = computed(() => {
      return masterCompetingDifficultyLevelGroupsActive.value.filter(x =>
        [entityType.value.toUpperCase(), "PAIR"].includes(x.rankedEntity)
      );
    });

    const {
      result,
      loading: competingDifficultyLevelsLoading,
      refetch: refetchCompetingDifficultyLevels
    } = useQuery(
      () => gql`
        query ${entityType.value}CompetingDifficultyLevels($id: ID) {
          ${entityType.value}(id: $id) {
            id
            competingDifficultyLevels {
              id
              personId
              person {
                id
                ${AppShowPersonNameWithIdentifiers.fragments.person}
              }
              horseId
              horse {
                id
                ${AppShowHorseName.fragments.horse}
              }
              status
              validFrom
              validUntil
              pointsBalance
              allowedMutations
              competingDifficultyLevelDefinitionId
              competingDifficultyLevelDefinition @client {
                id
                name
                competingDifficultyLevelGroupId
                usePositivePointsBalance
                useNegativePointsBalance
              }
            }
          }
        }
      `,
      () => {
        return {
          id: props.personId || props.horseId
        };
      }
    );

    const competingDifficultyLevels = computed(() => {
      function sortFn(a, b) {
        return new Date(b.validFrom) - new Date(a.validFrom);
      }

      const levelsByGroup = Object.entries(
        groupBy(
          cloneDeep(
            result.value?.[entityType.value]?.competingDifficultyLevels ?? []
          ),
          "competingDifficultyLevelDefinition.competingDifficultyLevelGroupId"
        )
      ).map(([groupId, levels]) => {
        const active = [];
        const inactive = [];

        levels.forEach(lvl => {
          if (lvl.status == "ACTIVE") active.push(lvl);
          else inactive.push(lvl);
        });

        return [groupId, [...active.sort(sortFn), ...inactive.sort(sortFn)]];
      });

      return levelsByGroup.reduce((obj, [groupId, levels]) => {
        const showPoints = levels.some(
          ({ competingDifficultyLevelDefinition: defin }) => {
            return (
              defin.usePositivePointsBalance || defin.useNegativePointsBalance
            );
          }
        );
        obj[groupId] = levels.map(level => {
          level.showPoints = showPoints;
          return level;
        });
        return obj;
      }, {});
    });

    function showPointsHeader(groupLevels = []) {
      return groupLevels.some(x => x.showPoints);
    }

    const competingLevelForm = ref();
    function openNewForm(group) {
      competingLevelForm.value.newCompetingLevel({
        personId: props.personId,
        horseId: props.horseId,
        competingDifficultyLevelGroupId: group.id
      });
    }

    function openEditForm(item) {
      competingLevelForm.value.editCompetingLevel(item);
    }

    async function deleteCompetingDifficultyLevel(item, evt) {
      const answer = await appConfirm(
        t(
          "common:competingDifficultyLevels.deleteLevel.modal.heading",
          "Delete level"
        ),
        t(
          "common:competingDifficultyLevels.deleteLevel.modal.question",
          "You are going to delete the selected level. Level can be deleted, when there are no results for this entity"
        )
      );
      if (!answer) return;

      const removeLoading = showLoadingButton(evt.target);

      try {
        await apolloClient.mutate({
          mutation: gql`
            mutation competingDifficultyLevelDelete($id: ID!) {
              competingDifficultyLevelDelete(input: { id: $id }) {
                success
              }
            }
          `,
          variables: {
            id: item.id
          }
        });
        refetchCompetingDifficultyLevels();
      } catch (error) {
        snackbarServerError(error, { level: "error" });
      } finally {
        removeLoading();
      }
    }

    return {
      showSpecificCompetingGroups,
      competingDifficultyLevelsLoading,
      refetchCompetingDifficultyLevels,
      competingDifficultyLevels,

      competingLevelForm,
      openNewForm,
      openEditForm,
      deleteCompetingDifficultyLevel,
      showPointsHeader
    };
  }
});
</script>
